import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Calendar, MapPin, Clock, Check, Timer, XCircle,
  RefreshCw, Users, Sun, Cloud, CloudSun, CloudRain,
  CloudDrizzle, CloudSnow, CloudLightning, Droplets,
  ChevronLeft, ChevronRight, FileText, ExternalLink
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchUpcomingEvents, updateEventResponse } from '../services/eventsApi';
import { fetchEventWeather } from '../services/weatherApi';
import BottomSheet from '../../components/common/BottomSheet';
import toast from 'react-hot-toast';
import { getWebOrigin } from '../../utils/helpers';

const RESPONSE_OPTIONS = [
  { status: 'available',   label: 'Going',     icon: Check,    color: 'text-green-500',  bg: 'bg-green-50 dark:bg-green-900/20',   border: 'border-green-500' },
  { status: 'join-late',   label: 'Late',      icon: Timer,    color: 'text-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-500' },
  { status: 'unavailable', label: "Can't go",  icon: XCircle,  color: 'text-red-500',    bg: 'bg-red-50 dark:bg-red-900/20',       border: 'border-red-500' },
];

const GOING_STATUSES = ['available', 'join-late'];

/** One card per event and person: a parent can have their own and each child's. */
const keyOf = (e) => `${e.id}:${e.forUser?.id ?? 'me'}`;
/** A child's card is answered for the child; the reader's own for themselves. */
const answeringFor = (e) => (e.forUser && !e.forUser.isMe ? e.forUser : null);
const WEATHER_ICONS  = { Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudSnow, CloudLightning };

function formatEventDate(startTime) {
  const date     = new Date(startTime);
  const now      = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isToday    = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();
  const dayLabel   = isToday ? 'Today'
    : isTomorrow ? 'Tomorrow'
    : date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  return { dayLabel, time };
}

function formatSlotTime(date) {
  return new Date(date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** 30-min slots from startTime to endTime */
function generateSlots(startTime) {
  if (!startTime) return [];
  const start = new Date(startTime);
  return [0, 1, 2].map(i => new Date(start.getTime() + i * 30 * 60 * 1000));
}

/** Count how many arrival times land on (or nearest to) each slot */
function buildSlotCounts(slots, arrivalTimes) {
  const counts = {};
  slots.forEach(s => { counts[s.toISOString()] = 0; });
  arrivalTimes.forEach(at => {
    const ms = new Date(at).getTime();
    const nearest = slots.reduce((b, s) =>
      Math.abs(s.getTime() - ms) < Math.abs(b.getTime() - ms) ? s : b
    );
    counts[nearest.toISOString()]++;
  });
  return counts;
}

/** Resolve the nearest slot ISO for a given arrival time */
function nearestSlotISO(arrivalTime, slots) {
  if (!arrivalTime || slots.length === 0) return null;
  const ms = new Date(arrivalTime).getTime();
  return slots.reduce((b, s) =>
    Math.abs(s.getTime() - ms) < Math.abs(b.getTime() - ms) ? s : b
  ).toISOString();
}

const NextEventPanel = ({ isOpen, onClose }) => {
  const { theme }  = useTheme();
  const navigate   = useNavigate();
  const [events,   setEvents]   = useState([]);
  const [weather,  setWeather]  = useState({});
  const [loading,  setLoading]  = useState(false);
  const [updating, setUpdating] = useState(null);
  const [index,    setIndex]    = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setIndex(0);
    try {
      const data = await fetchUpcomingEvents();
      const list = Array.isArray(data) ? data : [];
      setEvents(list);

      Promise.all(
        list.map(async e => {
          if (!e.venue?.lat || !e.venue?.lng) return [e.id, null];
          const w = await fetchEventWeather(e.venue.lat, e.venue.lng, e.startTime);
          return [e.id, w];
        })
      ).then(results => setWeather(Object.fromEntries(results))).catch(() => {});
    } catch {
      toast.error('Failed to load upcoming events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const handleResponse = async (card, status, arrivalTime = null) => {
    const key = keyOf(card);
    setUpdating(key);
    setEvents(prev => {
      const target   = prev.find(e => keyOf(e) === key);
      const wasGoing = GOING_STATUSES.includes(target?.myResponse);
      const isGoing  = GOING_STATUSES.includes(status);
      const delta    = wasGoing && !isGoing ? -1 : !wasGoing && isGoing ? 1 : 0;
      const prevArrival = target?.myArrivalTime;

      // The count and arrivals belong to the event, so every card for it moves;
      // the answer belongs to the one person whose card this is.
      return prev.map(e => {
        if (e.id !== card.id) return e;
        const arrivalTimes = [...(e.arrivalTimes || [])];
        if (prevArrival) {
          // Take back this person's arrival only — not everybody due at the same time.
          const i = arrivalTimes.findIndex(t =>
            Math.abs(new Date(t).getTime() - new Date(prevArrival).getTime()) <= 60000);
          if (i >= 0) arrivalTimes.splice(i, 1);
        }
        if (arrivalTime) arrivalTimes.push(arrivalTime);
        const shared = { availableCount: e.availableCount + delta, arrivalTimes };
        return keyOf(e) === key
          ? { ...e, ...shared, myResponse: status, myArrivalTime: arrivalTime }
          : { ...e, ...shared };
      });
    });
    try {
      await updateEventResponse(card.id, status, answeringFor(card)?.id ?? null, arrivalTime);
      toast.success('Availability updated');
    } catch {
      toast.error('Failed to update availability');
      load();
    } finally {
      setUpdating(null);
    }
  };

  const handleSlotSelect = (event, slotISO) => {
    const status = new Date(slotISO).getTime() <= new Date(event.startTime).getTime()
      ? 'available'
      : 'join-late';
    handleResponse(event, status, slotISO);
  };

  const handleSquadClick = (event) => {
    if (!event.group?.id) return;
    onClose();
    navigate(`/squads/${event.group.id}?tab=current-event`);
  };

  const event       = events[index] || null;
  const total       = events.length;
  const w           = event ? weather[event.id] : null;
  const WeatherIcon = w ? (WEATHER_ICONS[w.icon] || Cloud) : null;
  const isUpdating  = event ? updating === keyOf(event) : false;
  const child       = event ? answeringFor(event) : null;

  // Compute slots + counts for current event
  const slots = event ? generateSlots(event.startTime) : [];
  const slotCounts = event ? buildSlotCounts(slots, event.arrivalTimes || []) : {};
  const selectedSlotISO = event ? nearestSlotISO(event.myArrivalTime, slots) : null;
  const eventStartMs = event ? new Date(event.startTime).getTime() : 0;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <Calendar className={`w-5 h-5 ${theme.primary}`} />
            <span className={`font-semibold ${theme.text}`}>Upcoming Events</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className={`p-1.5 rounded-lg ${theme.cardHover}`}>
              <RefreshCw className={`w-4 h-4 ${theme.textMuted}`} />
            </button>
            <button onClick={onClose} className={`p-1.5 rounded-lg ${theme.cardHover}`}>
              <X className={`w-4 h-4 ${theme.textMuted}`} />
            </button>
          </div>
        </div>

        <div className={`h-px ${theme.border} bg-current opacity-10 mx-4`} />

        {/* Content */}
        <div className="px-4 py-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className={`w-6 h-6 border-2 border-t-transparent rounded-full animate-spin ${theme.primary.replace('text-', 'border-')}`} />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className={`text-sm ${theme.textMuted}`}>No upcoming events</p>
            </div>
          ) : (
            <>
              {/* Event card */}
              <div className={`rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-3`}>
                {/* Squad + available count */}
                <div className="flex items-center justify-between mb-1">
                  <button
                    onClick={() => handleSquadClick(event)}
                    className={`text-xs font-medium ${theme.primary} hover:underline text-left`}
                  >
                    {event.group?.name || 'Unknown Squad'}
                    {event.group?.sportName ? ` · ${event.group.sportName}` : ''}
                  </button>
                  <span className={`flex items-center gap-1 text-xs font-medium ${event.availableCount > 0 ? 'text-green-600 dark:text-green-400' : theme.textMuted}`}>
                    <Users className="w-3 h-3" />
                    {event.availableCount} going
                  </span>
                </div>

                {/* Whose invitation this is, when it is a child's */}
                {child && (
                  <p className={`flex items-center gap-1 text-xs font-semibold ${theme.text} mb-1`}>
                    <Users className="w-3 h-3" />
                    For {child.name}
                  </p>
                )}

                {/* Subject */}
                <p className={`font-semibold text-sm ${theme.text} mb-2`}>{event.subject}</p>

                {/* Date + Location + Weather */}
                <div className={`flex flex-wrap gap-x-3 gap-y-1 text-xs ${theme.textMuted} mb-3`}>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatEventDate(event.startTime).dayLabel} · {formatEventDate(event.startTime).time}
                  </span>
                  {event.venue?.name || event.venue?.city ? (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {event.venue.name || event.venue.city}
                    </span>
                  ) : null}
                  {w && (
                    <span className="flex items-center gap-1">
                      <WeatherIcon className="w-3 h-3" />
                      {w.tempMax}°F
                      {w.precipPct > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Droplets className="w-3 h-3" />
                          {w.precipPct}%
                        </span>
                      )}
                    </span>
                  )}
                </div>

                {/* Permit link */}
                {event.permitUrl && (
                  <a
                    href={`${getWebOrigin()}/permits/${event.permitUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mb-3"
                  >
                    <FileText className="w-3 h-3" />
                    View Permit
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}

                {/* Going / Late / Can't go buttons */}
                <div className="flex gap-2 mb-3">
                  {RESPONSE_OPTIONS.map(({ status, label, icon: Icon, color, bg, border }) => {
                    const isActive = event.myResponse === status;
                    return (
                      <button
                        key={status}
                        disabled={isUpdating}
                        onClick={() => handleResponse(event, status)}
                        className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium transition-all
                          ${isActive ? `${bg} ${border} ${color}` : `${theme.cardHover} ${theme.border} ${theme.textMuted}`}
                          ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* ── Arrival time picker ── */}
                {slots.length > 0 && (
                  <div>
                    <p className={`text-xs font-semibold mb-2 ${theme.textSecondary}`}>
                      {child ? `When will ${child.name} arrive?` : 'When will you arrive?'}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map(slot => {
                        const iso     = slot.toISOString();
                        const onTime  = slot.getTime() <= eventStartMs;
                        const count   = slotCounts[iso] || 0;
                        const isSelected = selectedSlotISO === iso;

                        return (
                          <button
                            key={iso}
                            disabled={isUpdating}
                            onClick={() => handleSlotSelect(event, iso)}
                            className={`flex flex-col items-center justify-center gap-0.5 rounded-xl border py-3 px-1 transition-all
                              ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
                              ${isSelected
                                ? onTime
                                  ? 'bg-green-100 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-400'
                                  : 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-500 text-yellow-700 dark:text-yellow-400'
                                : `${theme.backgroundSecondary} ${theme.border} ${theme.textSecondary} active:opacity-70`
                              }`}
                          >
                            {/* Time label */}
                            <span className={`text-sm font-semibold leading-tight ${isSelected ? '' : theme.text}`}>
                              {formatSlotTime(slot)}
                            </span>

                            {/* On time / Late tag */}
                            <span className={`text-[10px] leading-tight ${
                              isSelected
                                ? 'opacity-80'
                                : onTime ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
                            }`}>
                              {onTime ? 'On time' : 'Late'}
                            </span>

                            {/* People count */}
                            <span className={`flex items-center gap-0.5 text-[10px] mt-0.5 ${
                              count > 0
                                ? isSelected ? 'opacity-90' : 'text-blue-500 dark:text-blue-400'
                                : theme.textMuted
                            }`}>
                              <Users className="w-2.5 h-2.5" />
                              {count} {count === 1 ? 'person' : 'people'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Prev / Next navigation */}
              {total > 1 && (
                <div className="flex items-center justify-between mt-3">
                  <button
                    onClick={() => setIndex(i => Math.max(0, i - 1))}
                    disabled={index === 0}
                    className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border ${theme.border} transition-all
                      ${index === 0 ? 'opacity-30 cursor-not-allowed' : theme.cardHover}`}
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>

                  <div className="flex gap-1.5">
                    {events.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setIndex(i)}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${i === index ? 'bg-blue-500 w-3' : 'bg-gray-300 dark:bg-gray-600'}`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => setIndex(i => Math.min(total - 1, i + 1))}
                    disabled={index === total - 1}
                    className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border ${theme.border} transition-all
                      ${index === total - 1 ? 'opacity-30 cursor-not-allowed' : theme.cardHover}`}
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
    </BottomSheet>
  );
};

export default NextEventPanel;
